/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";

import { Label, Stack, StackItem, Text } from "@fluentui/react";

import {
  bodyColumnStackItemStyles,
  bodyContainerStackStyles,
  bodyContainerStackTokens,
  bodyRowStackStyles,
  headerStackStyles,
  headerTextStyles,
  labelStyles,
  ContainerWrapper,
} from "Components/Common/SharedTableStyles.styles";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { fetchApplicationQuestionsBySelectedApplicationId } from "Dataflows/ApplicationDetails/ApplicationDetailsAction";
import {
  getApplicationQuestionsBySelectedApplicationId,
  getIsLoadingApplicationQuestionsBySelectedApplicationId,
  getUserProfileInfoBySelectedApplicationId,
} from "Dataflows/ApplicationDetails/ApplicationDetailsSelector";
import { IApplicationQuestionResponse } from "Interfaces/Application/IApplicationQuestionResponse";
import { formatDate } from "Utility/FormatHelper";
import { GetApplicationQuestionsGroupBySectionAndByQuestionId } from "Utility/Utils";
import { OrderBy, QuestionsFormSectionStringEnum } from "Constants/ApplicationConstants";

const ProfileInformation = (): ReactElement => {
  const dispatch = useDispatch();
  const applicationQuestions: IApplicationQuestionResponse[] | undefined = useSelector(
    getApplicationQuestionsBySelectedApplicationId
  );
  const isLoading = useSelector(getIsLoadingApplicationQuestionsBySelectedApplicationId);
  const userProfileInfo = useSelector(getUserProfileInfoBySelectedApplicationId);
  const applicationQuestionsGroupBySectionAndByQuestionId =
    GetApplicationQuestionsGroupBySectionAndByQuestionId(applicationQuestions);
  const sortedApplicationQuestions = _.orderBy(
    applicationQuestionsGroupBySectionAndByQuestionId[
      QuestionsFormSectionStringEnum.PROFILE_INFORMATION
    ],
    [OrderBy]
  );

  useEffect(() => {
    if (!applicationQuestions && !isLoading && !!userProfileInfo?.applicationFormId) {
      dispatch(
        fetchApplicationQuestionsBySelectedApplicationId(
          userProfileInfo.applicationFormId,
          TenantTypesEnum.MVP
        )
      );
    }
  }, [applicationQuestions, dispatch, isLoading]);
  const profileInfo = useSelector(getUserProfileInfoBySelectedApplicationId);
  return (
    <Stack>
      <ContainerWrapper>
        <Stack styles={headerStackStyles}>
          <Text styles={headerTextStyles}>Profile Information</Text>
        </Stack>
        <Stack styles={bodyContainerStackStyles} tokens={bodyContainerStackTokens}>
          <Stack horizontal styles={bodyRowStackStyles}>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>First Name</Label>
              <Text>{profileInfo?.firstName}</Text>
            </StackItem>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Last Name</Label>
              <Text>{profileInfo?.lastName}</Text>
            </StackItem>
          </Stack>
          <Stack horizontal styles={bodyRowStackStyles}>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Title</Label>
              <Text>{profileInfo?.titleName}</Text>
            </StackItem>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Date of birth</Label>
              {!!profileInfo?.dateOfBirth ? (
                <Text>{formatDate(new Date(profileInfo?.dateOfBirth))}</Text>
              ) : (
                <></>
              )}
            </StackItem>
          </Stack>
          <Stack horizontal styles={bodyRowStackStyles}>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Primary Email Address</Label>
              <Text>{profileInfo?.siteEmailAddress}</Text>
            </StackItem>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Preferred Contact Email Address</Label>
              <Text>{profileInfo?.preferredContactEmailAddress}</Text>
            </StackItem>
          </Stack>
          <Stack horizontal styles={bodyRowStackStyles}>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Gender</Label>
              <Text>{profileInfo?.genderName}</Text>
            </StackItem>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Pronoun</Label>
              <Text>{profileInfo?.genderPronounPreferenceName}</Text>
            </StackItem>
          </Stack>
          <Stack horizontal styles={bodyRowStackStyles}>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Country/Region</Label>
              <Text>{profileInfo?.addressCountryOrRegionName}</Text>
            </StackItem>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>State/Province</Label>
              <Text>{profileInfo?.addressStateOrProvince}</Text>
            </StackItem>
          </Stack>
          <Stack horizontal styles={bodyRowStackStyles}>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Phone Code</Label>
              <Text>{profileInfo?.primaryPhoneCountryOrRegionName}</Text>
            </StackItem>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Phone Number</Label>
              <Text>{profileInfo?.primaryPhone}</Text>
            </StackItem>
          </Stack>
          <Stack horizontal styles={bodyRowStackStyles}>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Languages</Label>
              <Text>{profileInfo?.languages?.join(", ")}</Text>
            </StackItem>
          </Stack>
          <Stack horizontal styles={bodyRowStackStyles}>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Preferred Programming Language</Label>
              <Text>{profileInfo?.userProfileProgramLanguage?.join(", ")}</Text>
            </StackItem>
            <StackItem styles={bodyColumnStackItemStyles}>
              <Label styles={labelStyles}>Functional Role</Label>
              <Text>{profileInfo?.userProfileFunctionalRole?.join(", ")}</Text>
            </StackItem>
          </Stack>
          {profileInfo?.userProfileSocialNetwork.map((x) => (
            <Stack key={x.id} horizontal styles={bodyRowStackStyles}>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Network</Label>
                <Text>{x?.socialNetworkName}</Text>
              </StackItem>
              <StackItem styles={bodyColumnStackItemStyles}>
                <Label styles={labelStyles}>Link/URL</Label>
                <Text>{x?.socialNetworkImageLink}</Text>
              </StackItem>
            </Stack>
          ))}
          <Stack>
            {sortedApplicationQuestions.map((question) => (
              <StackItem key={question.questionId}>
                <Label styles={labelStyles}>{question.questionText}</Label>
                <Text>{question.questionResponseText}</Text>
              </StackItem>
            ))}
          </Stack>
          <Stack>
            <StackItem>
              <Label styles={labelStyles}>
                {profileInfo?.leveragingNetworkAndInfluenceQuestion}
              </Label>
              <Text>{profileInfo?.leveragingNetworkAndInfluenceQuestionResponse}</Text>
            </StackItem>
          </Stack>
        </Stack>
      </ContainerWrapper>
    </Stack>
  );
};

export default ProfileInformation;
